// Passkey login (roadmap 3.1): first passkey with the setup code, then Face ID; recovery with the paper key.
import { useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { api } from './api';
import { JarvisCore, Label, Panel } from './ui';

type Mode = 'login' | 'setup' | 'recover' | 'recovery-key';

export function Login({ hasPasskeys, recoveryOnly, onDone }: { hasPasskeys: boolean; recoveryOnly?: boolean; onDone: () => void }) {
  const [mode, setMode] = useState<Mode>(recoveryOnly ? 'recover' : hasPasskeys ? 'login' : 'setup');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');

  const run = async (fn: () => Promise<void>) => {
    setErr(''); setBusy(true);
    try { await fn(); } catch (e: any) { setErr(e?.name === 'NotAllowedError' ? 'Megszakítva.' : e?.message || 'Hiba történt.'); } finally { setBusy(false); }
  };

  const login = () => run(async () => {
    const { options, ticket } = await api('/api/auth/login/options', {});
    const response = await startAuthentication({ optionsJSON: options });
    await api('/api/auth/login/verify', { ticket, response });
    onDone();
  });

  const register = (proof: { setupCode?: string; recoveryKey?: string }) => run(async () => {
    const { options, ticket } = await api('/api/auth/register/options', proof);
    const response = await startRegistration({ optionsJSON: options });
    const label = /iPhone|iPad/.test(navigator.userAgent) ? 'iPhone' : /Windows/.test(navigator.userAgent) ? 'Windows' : 'eszköz';
    const r = await api<{ recoveryKey: string | null }>('/api/auth/register/verify', { ticket, response, label });
    if (r.recoveryKey) { setRecoveryKey(r.recoveryKey); setMode('recovery-key'); } else onDone();
  });

  const recover = () => run(async () => {
    await api('/api/auth/recover', { recoveryKey: code });
    await register({ recoveryKey: code });
  });

  return (
    <main className="login">
      <JarvisCore size={140} />
      <h1 className="greet">Jarvis</h1>
      {mode === 'login' && (
        <Panel c="amber" className="login-panel">
          <Label c="amber">Belépés</Label>
          <p>Face ID-val, ujjlenyomattal vagy a jelszókezelőd passkeyével.</p>
          <button className="j-btn j-btn--primary" disabled={busy} onClick={login}>Belépés passkeyjel</button>
          <button className="j-btn linkish" onClick={() => setMode('recover')}>Elvesztettem az eszközeimet</button>
        </Panel>
      )}
      {mode === 'setup' && (
        <Panel c="amber" className="login-panel">
          <Label c="amber">Első belépés</Label>
          <p>Írd be a beállító kódot, amit a szerveren kértél, és a telefonod létrehozza a passkeyt.</p>
          <input className="j-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="xxxxx-xxxxx-xxxxx-xxxxx-xxxxx" autoCapitalize="none" autoComplete="one-time-code" spellCheck={false} />
          <button className="j-btn j-btn--primary" disabled={busy || code.length < 10} onClick={() => register({ setupCode: code })}>Passkey létrehozása</button>
        </Panel>
      )}
      {mode === 'recover' && (
        <Panel c="coral" className="login-panel">
          <Label c="coral">Vészkulcs</Label>
          <p>Írd be a papíron tartott vészkulcsot. Utána új passkeyt veszünk fel erre az eszközre.</p>
          <input className="j-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="vészkulcs" autoCapitalize="none" spellCheck={false} />
          <button className="j-btn j-btn--primary" disabled={busy || code.length < 10} onClick={recover}>Új passkey felvétele</button>
          {hasPasskeys && !recoveryOnly && <button className="j-btn linkish" onClick={() => setMode('login')}>Vissza</button>}
        </Panel>
      )}
      {mode === 'recovery-key' && (
        <Panel c="coral" className="login-panel">
          <Label c="coral">A vészkulcsod — csak most látod</Label>
          <p>Írd fel <b>papírra</b>, és tedd biztos helyre. Ha minden eszközöd elveszne, ezzel tudsz újra belépni.</p>
          <div className="recovery j-num">{recoveryKey}</div>
          <button className="j-btn j-btn--primary" onClick={onDone}>Felírtam</button>
        </Panel>
      )}
      {err && <p className="err">{err}</p>}
    </main>
  );
}
